import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { trackEvent } from "../analytics/ga4";
import { scrollToAnchor } from "../utils/scroll";

const links = [
  { to: "/leadership", key: "leadership" },
  { to: "/team", key: "teams" },
  { to: "/organisation", key: "organisation" },
  { to: "/strategie", key: "strategie" },
  { to: "/ueber-mich", key: "about" },
];

export const LangToggle = ({ dark = false, testId = "lang-toggle" }) => {
  const { lang, toggle } = useLanguage();
  return (
    <button
      data-testid={testId}
      onClick={toggle}
      aria-label="Switch language / Sprache wechseln"
      className={`text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
        dark ? "text-ink/70 hover:text-ink" : "text-white/70 hover:text-white"
      }`}
    >
      <span className={lang === "de" ? "opacity-100" : "opacity-40"}>DE</span>
      <span className="mx-1.5 opacity-30">/</span>
      <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
    </button>
  );
};

export const Navigation = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 48);
      if (y > 160 && y > last + 4) setHidden(true);
      else if (y < last - 4 || y <= 160) setHidden(false);
      last = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goContact = (e) => {
    e.preventDefault();
    trackEvent("contact_cta_click", { placement: open ? "mobile_navigation" : "navigation" });
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#kontakt");
    } else {
      scrollToAnchor("kontakt");
    }
  };

  const dark = scrolled && !open;

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-700 ${
          hidden && !open ? "-translate-y-full" : "translate-y-0"
        } ${
          dark
            ? "bg-paper/90 text-ink shadow-[0_1px_0_rgba(6,47,55,0.08)] backdrop-blur-md"
            : "bg-transparent text-white"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 sm:px-12 md:px-16">
          <Link to="/" data-testid="nav-logo-link" aria-label="David Frank — Home" className="relative z-50">
            <Logo dark={dark} compact />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `group relative text-[12px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    dark ? "text-ink/70 hover:text-ink" : "text-white/75 hover:text-white"
                  } ${isActive ? (dark ? "!text-ink" : "!text-white") : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    {t.nav[l.key]}
                    <span className={`absolute -bottom-1.5 left-0 h-px bg-wave transition-all duration-500 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-8 lg:flex">
            <LangToggle dark={dark} testId="lang-toggle-desktop" />
            <a
              href="/#kontakt"
              onClick={goContact}
              data-testid="nav-contact-cta"
              className={`border px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.22em] transition-all duration-500 ${
                dark
                  ? "border-ink/25 text-ink hover:bg-ink hover:text-white"
                  : "border-white/30 text-white hover:bg-white hover:text-ink"
              }`}
            >
              {t.nav.contact}
            </a>
          </div>

          <div className="flex items-center gap-6 lg:hidden">
            <LangToggle dark={dark && !open} testId="lang-toggle-mobile" />
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-label={open ? t.nav.close : t.nav.menu}
              className={`relative z-50 flex h-10 w-10 flex-col items-center justify-center gap-1.5 ${
                open ? "text-white" : dark ? "text-ink" : "text-white"
              }`}
            >
              <span className={`block h-px w-6 bg-current transition-all duration-500 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
              <span className={`block h-px w-6 bg-current transition-all duration-500 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={open} onClose={() => setOpen(false)} goContact={goContact} />
    </>
  );
};
